import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "@/services/supabase-client.service";
import {
    getAIPhoneCalls,
    getPhoneStats,
    getRecentCalls,
    markAIPhoneCallDone,
    forwardAIPhoneCallToDoctor,
    archiveAIPhoneCall,
} from "@/services/ai-phone.service";
import type { AIPhoneCallFilters, AIPhoneCall, RecentCall } from "@/types/ai-phone";
import { useToast } from "@/components/shared/Toast";

// --- Query Keys ---
export const phoneKeys = {
    all: ["ai-phone"] as const,
    list: (practiceId?: string, filters?: AIPhoneCallFilters) =>
        [...phoneKeys.all, "list", practiceId, filters].filter(Boolean),
    recent: (practiceId?: string) => [...phoneKeys.all, "recent", practiceId] as const,
    stats: (practiceId?: string) => [...phoneKeys.all, "stats", practiceId] as const,
};

// --- Realtime Subscription Hook ---
export function usePhoneRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`phone-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "ai_phone_calls",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: phoneKeys.all });
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}

// --- Queries ---
export function usePhoneStatsQuery(practiceId?: string) {
    return useQuery({
        queryKey: phoneKeys.stats(practiceId),
        queryFn: () => getPhoneStats(practiceId!),
        enabled: !!practiceId,
    });
}

export function usePhoneCallsQuery(practiceId?: string, filters: AIPhoneCallFilters = {}) {
    return useQuery({
        queryKey: phoneKeys.list(practiceId, filters),
        queryFn: () => getAIPhoneCalls(practiceId!, filters),
        enabled: !!practiceId,
    });
}

export function useRecentCallsQuery(practiceId?: string, limit = 5) {
    return useQuery({
        queryKey: phoneKeys.recent(practiceId),
        queryFn: () => getRecentCalls(practiceId!, limit),
        enabled: !!practiceId,
    });
}

// --- Mutations with Optimistic Updates ---
export function useMarkPhoneCallDoneMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (callId: string) => markAIPhoneCallDone(practiceId!, callId),
        onMutate: async (callId) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: phoneKeys.all });

            const previousLists = queryClient.getQueriesData<AIPhoneCall[]>({
                queryKey: phoneKeys.list(practiceId),
            });
            const previousRecent = queryClient.getQueriesData<RecentCall[]>({
                queryKey: phoneKeys.recent(practiceId),
            });

            queryClient.setQueriesData<AIPhoneCall[]>(
                { queryKey: phoneKeys.list(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) =>
                        c.id === callId
                            ? { ...c, status: "completed", outcome: "resolved_by_practice" }
                            : c,
                    );
                },
            );

            return { previousLists, previousRecent };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context) {
                if (context.previousLists) {
                    context.previousLists.forEach(([key, data]: [any, any]) =>
                        queryClient.setQueryData(key, data),
                    );
                }
                if (context.previousRecent) {
                    context.previousRecent.forEach(([key, data]: [any, any]) =>
                        queryClient.setQueryData(key, data),
                    );
                }
            }
            addToast({ type: "error", title: "Aktion fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: phoneKeys.all });
        },
    });
}

export function useForwardPhoneCallMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (callId: string) => forwardAIPhoneCallToDoctor(practiceId!, callId),
        onMutate: async (callId) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: phoneKeys.all });

            const previousLists = queryClient.getQueriesData<AIPhoneCall[]>({
                queryKey: phoneKeys.list(practiceId),
            });

            queryClient.setQueriesData<AIPhoneCall[]>(
                { queryKey: phoneKeys.list(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map((c) =>
                        c.id === callId
                            ? { ...c, status: "escalated", outcome: "forwarded_to_doctor" }
                            : c,
                    );
                },
            );

            return { previousLists };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousLists) {
                context.previousLists.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Weiterleiten fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: phoneKeys.all });
        },
    });
}

export function useArchivePhoneCallMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (callId: string) => archiveAIPhoneCall(practiceId!, callId),
        onMutate: async (callId) => {
            if (!practiceId) return {};
            await queryClient.cancelQueries({ queryKey: phoneKeys.all });

            const previousLists = queryClient.getQueriesData<AIPhoneCall[]>({
                queryKey: phoneKeys.list(practiceId),
            });

            queryClient.setQueriesData<AIPhoneCall[]>(
                { queryKey: phoneKeys.list(practiceId) },
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.filter((c) => c.id !== callId);
                },
            );

            return { previousLists };
        },
        onError: (_err, _variables, context: any) => {
            if (practiceId && context?.previousLists) {
                context.previousLists.forEach(([key, data]: [any, any]) =>
                    queryClient.setQueryData(key, data),
                );
            }
            addToast({ type: "error", title: "Archivieren fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: phoneKeys.all });
        },
    });
}
